import type { FC, SVGProps } from "react";
import { getPositiveNumber } from "../deckGeometry";
import KkScrewGraphic, { getKkScrewLayout, type KkScrewSize, type MillimetersToPixels } from "./KkScrew";

type TerralockProps = SVGProps<SVGSVGElement> & {
  baseHeight?: number;
  boardScrew?: Partial<KkScrewSize> | null;
  postLeftOffset?: number;
  postSize?: number;
  substructureScrew?: Partial<KkScrewSize> | null;
  screwVisibleLength?: number | null;
  topHeight?: number;
  toPixels?: MillimetersToPixels;
};

const ARTWORK_WIDTH = 60.2;
const ARTWORK_VISIBLE_BOTTOM = 13.4;
const ARTWORK_BASE_HEIGHT = 7.78;
const ARTWORK_POST_RIGHT_X = ARTWORK_WIDTH - 44.72;
const ARTWORK_LEFT_BASE_RECT_CENTER_X = ARTWORK_WIDTH - (45.67 + (58.33 - 45.67) / 2);
const ARTWORK_FILL_COLOR = "#575756";
const ARTWORK_STROKE_COLOR = "#1d1d1b";
const ARTWORK_STROKE_WIDTH = 0.25;

const dashedStrokeProps = {
  fill: ARTWORK_FILL_COLOR,
  stroke: ARTWORK_STROKE_COLOR,
  strokeDasharray: "1.57 1.57",
  strokeLinejoin: "round" as const,
  strokeWidth: ARTWORK_STROKE_WIDTH,
};

const filledStrokeProps = {
  fill: ARTWORK_FILL_COLOR,
  stroke: ARTWORK_STROKE_COLOR,
  strokeLinejoin: "round" as const,
  strokeWidth: ARTWORK_STROKE_WIDTH,
};

const outlineStrokeProps = {
  fill: "none",
  stroke: ARTWORK_STROKE_COLOR,
  strokeLinejoin: "round" as const,
  strokeWidth: ARTWORK_STROKE_WIDTH,
};

const Terralock: FC<TerralockProps> = ({
  baseHeight,
  boardScrew,
  postLeftOffset,
  postSize,
  substructureScrew,
  screwVisibleLength,
  topHeight,
  toPixels,
  x,
  y,
  width,
  height,
  viewBox,
  preserveAspectRatio,
  ...props
}) => {
  const safeWidth = getPositiveNumber(width, ARTWORK_WIDTH);
  const artworkScale = safeWidth / ARTWORK_WIDTH;
  const safeBaseHeight = getPositiveNumber(baseHeight, ARTWORK_BASE_HEIGHT * artworkScale);
  const safeTopHeight = getPositiveNumber(
    topHeight,
    getPositiveNumber(height, ARTWORK_VISIBLE_BOTTOM * artworkScale) - safeBaseHeight,
  );
  const totalHeight = getPositiveNumber(height, safeTopHeight + safeBaseHeight);
  const safePostSize = getPositiveNumber(postSize, 1.92 * artworkScale);
  const safePostLeftOffset = getPositiveNumber(postLeftOffset, ARTWORK_POST_RIGHT_X * artworkScale - safePostSize);
  const artworkX = safePostLeftOffset + safePostSize - ARTWORK_POST_RIGHT_X * artworkScale;
  const artworkY = totalHeight - ARTWORK_VISIBLE_BOTTOM * artworkScale;
  const screwLayout =
    toPixels && boardScrew
      ? getKkScrewLayout({
          screw: boardScrew,
          visibleLength: screwVisibleLength,
          showHead: false,
          rotation: 180,
          toPixels,
          centerX: 0,
          anchor: { type: "top", y: 0 },
        })
      : null;
  const screwWidth = screwLayout?.width ?? safePostSize * 2.8;
  const screwSpacing = Math.max(safePostSize, screwWidth * 0.25);
  const screwAreaStart = safePostLeftOffset + safePostSize + screwWidth / 2 + screwSpacing;
  const screwAreaEnd = Math.max(screwAreaStart + screwWidth * 0.85, safeWidth - screwWidth / 2 - screwSpacing);
  const screwCenters = [screwAreaStart, screwAreaEnd];
  const substructureScrewCenterX = artworkX + ARTWORK_LEFT_BASE_RECT_CENTER_X * artworkScale;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox ?? `0 0 ${safeWidth} ${totalHeight}`}
      aria-hidden="true"
      x={x}
      y={y}
      width={width ?? safeWidth}
      height={height ?? totalHeight}
      overflow="visible"
      preserveAspectRatio={preserveAspectRatio ?? "none"}
      {...props}
    >
      {toPixels &&
        boardScrew &&
        screwCenters.map((centerX) => (
          <KkScrewGraphic
            key={centerX}
            screw={boardScrew}
            visibleLength={screwVisibleLength}
            showHead={false}
            rotation={180}
            toPixels={toPixels}
            centerX={centerX}
            topY={0}
          />
        ))}
      {toPixels && substructureScrew && (
        <KkScrewGraphic
          screw={substructureScrew}
          showHead={false}
          toPixels={toPixels}
          centerX={substructureScrewCenterX}
          topY={totalHeight}
        />
      )}
      <g transform={`translate(${artworkX} ${artworkY}) scale(${artworkScale})`}>
        <g transform={`matrix(-1,0,0,1,${ARTWORK_WIDTH},0)`}>
          <rect x={1.14} y={5.6} width={58.22} height={7.78} fill={ARTWORK_FILL_COLOR} />
          <polyline points="44.71 5.63 44.71 .74 46.63 .74 46.63 5.63" {...filledStrokeProps} />
          <rect x={44.72} y={0.74} width={1.92} height={4.89} {...filledStrokeProps} />
          <rect x={0.84} y={5.63} width={58.46} height={7.78} {...filledStrokeProps} />
          <line x1={1.83} y1={5.63} x2={1.83} y2={13.4} {...filledStrokeProps} />
          <line x1={58.33} y1={6.59} x2={45.67} y2={6.59} {...dashedStrokeProps} />
          <polyline points="43.71 13.4 43.71 6.7 43.71 6.59 44.71 5.63" {...filledStrokeProps} />
          <line x1={1.83} y1={6.59} x2={43.65} y2={6.57} {...dashedStrokeProps} />
          <line x1={58.33} y1={5.63} x2={58.33} y2={13.4} {...filledStrokeProps} />
          <polyline points="45.67 13.4 45.67 6.59 44.71 5.63 44.71 13.4" {...outlineStrokeProps} />
        </g>
      </g>
    </svg>
  );
};

export default Terralock;
